import { Component, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy, input, output } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { LocationOption, LocationPriceConfig } from '../../../../../model/subscription-billing';
import { MultiValueSelectorComponent, MultiSelectorOption } from '../../../../common/multi-value-selector/multi-value-selector.component';

// Re-export for convenience
export { LocationOption, LocationPriceConfig } from '../../../../../model/subscription-billing';

@Component({
  selector: 'app-location-price-config',
  imports: [FormsModule, MultiValueSelectorComponent],
  templateUrl: './location-price-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './location-price-config.component.scss'
})
export class LocationPriceConfigComponent implements OnInit, OnChanges {
  // Inputs
  readonly locations = input<LocationOption[]>([]);
  readonly defaultPrice = input<number>(0);
  readonly billingCycleLabel = input<string>('mo');
  readonly showLocationSection = input<boolean>(true);
  readonly showPriceSection = input<boolean>(true);

  // Initial values (for edit mode)
  readonly initialApplyToAll = input<boolean>(true);
  readonly initialSelectedLocationIds = input<string[]>([]);
  readonly initialUseDefaultPrice = input<boolean>(true);
  readonly initialCustomPrice = input<number | null>(null);

  // Output
  readonly configChanged = output<LocationPriceConfig>();

  // Internal state
  applyToAllLocations = true;
  selectedLocationIds = new Set<string>();
  useDefaultPrice = true;
  customPrice: number | null = null;

  // Multi-selector options
  locationOptions: MultiSelectorOption[] = [];
  selectedLocationIdArray: string[] = [];

  ngOnInit(): void {
    this.buildLocationOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations']) {
      this.buildLocationOptions();
    }
    if (changes['initialApplyToAll'] && !changes['initialApplyToAll'].firstChange === false) {
      this.applyToAllLocations = this.initialApplyToAll();
    }
    if (changes['initialSelectedLocationIds']) {
      this.selectedLocationIds = new Set(this.initialSelectedLocationIds());
      this.selectedLocationIdArray = [...this.initialSelectedLocationIds()];
    }
    if (changes['initialUseDefaultPrice'] && !changes['initialUseDefaultPrice'].firstChange === false) {
      this.useDefaultPrice = this.initialUseDefaultPrice();
    }
    if (changes['initialCustomPrice']) {
      this.customPrice = this.initialCustomPrice();
    }
  }

  private buildLocationOptions(): void {
    this.locationOptions = this.locations().map(loc => ({
      id: loc.id,
      label: loc.name,
      icon: 'storefront'
    }));
  }

  get hasLocations(): boolean {
    return this.locations().length > 0;
  }

  get selectedLocationsCount(): number {
    return this.selectedLocationIds.size;
  }

  get effectiveUnitPrice(): number {
    if (this.useDefaultPrice || this.customPrice === null) {
      return this.defaultPrice();
    }
    return this.customPrice;
  }

  get quantity(): number {
    if (!this.showLocationSection() || !this.hasLocations) return 1;
    return this.applyToAllLocations ? this.locations().length : Math.max(1, this.selectedLocationsCount);
  }

  get totalPrice(): number {
    return this.effectiveUnitPrice * this.quantity;
  }

  get discountAmount(): number {
    if (this.useDefaultPrice || this.customPrice === null || this.customPrice >= this.defaultPrice()) {
      return 0;
    }
    return this.defaultPrice() - this.customPrice;
  }

  isLocationSelected(locationId: string): boolean {
    return this.selectedLocationIds.has(locationId);
  }

  toggleLocation(locationId: string): void {
    if (this.selectedLocationIds.has(locationId)) {
      this.selectedLocationIds.delete(locationId);
    } else {
      this.selectedLocationIds.add(locationId);
    }
    this.emitChange();
  }

  onLocationModeChange(mode: string): void {
    this.applyToAllLocations = mode === 'all';
    if (this.applyToAllLocations) {
      this.selectedLocationIds.clear();
      this.selectedLocationIdArray = [];
    }
    this.emitChange();
  }

  onLocationIdsChange(ids: string[]): void {
    this.selectedLocationIds = new Set(ids);
    this.selectedLocationIdArray = ids;
    this.emitChange();
  }

  onPriceTypeChange(useDefault: boolean): void {
    this.useDefaultPrice = useDefault;
    if (!useDefault && this.customPrice === null) {
      this.customPrice = this.defaultPrice();
    }
    this.emitChange();
  }

  onCustomPriceChange(): void {
    this.emitChange();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  private emitChange(): void {
    this.configChanged.emit({
      applyToAllLocations: this.applyToAllLocations,
      selectedLocationIds: Array.from(this.selectedLocationIds),
      useDefaultPrice: this.useDefaultPrice,
      customPrice: this.customPrice
    });
  }

  // Public method to get current config
  getConfig(): LocationPriceConfig {
    return {
      applyToAllLocations: this.applyToAllLocations,
      selectedLocationIds: Array.from(this.selectedLocationIds),
      useDefaultPrice: this.useDefaultPrice,
      customPrice: this.customPrice
    };
  }
}